/**
 * Menu Item Card Component
 * Compact card for displaying menu item in grid view
 */

import { Edit2, Package, Trash2 } from 'lucide-react';
import React from 'react';
import { MenuItem } from '../../../../types/menu';

interface MenuItemCardProps {
  canManageMenu?: boolean;
  item: MenuItem;
  onEdit: (item: MenuItem) => void;
  onDelete: (item: MenuItem) => void;
  formatPrice: (price: number) => string;
}

const StatusBadge: React.FC<{ status: MenuItem['status'] }> = ({ status }) => {
  const statusStyles = {
    active: 'bg-white/90 text-green-700 border-green-200',
    out_of_stock: 'bg-white/90 text-amber-700 border-amber-200',
    inactive: 'bg-white/90 text-gray-700 border-gray-200',
  };

  return (
    <span
      className={`absolute top-2 right-2 px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wide border ${statusStyles[status]}`}
    >
      {status === 'out_of_stock' ? 'OOS' : status}
    </span>
  );
};

const ItemImage: React.FC<{ imageUrl?: string; name: string }> = ({
  imageUrl,
  name,
}) => {
  if (imageUrl) {
    return (
      <img
        src={imageUrl}
        alt={name}
        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
      />
    );
  }

  return (
    <div className="w-full h-full flex items-center justify-center text-gray-300">
      <Package size={32} />
    </div>
  );
};

const ActionButtons: React.FC<{
  onEdit: () => void;
  onDelete: () => void;
}> = ({ onEdit, onDelete }) => (
  <div className="mt-auto pt-2 grid grid-cols-2 gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
    <button
      type="button"
      onClick={onEdit}
      className="flex items-center justify-center py-1.5 text-xs font-medium text-gray-700 bg-gray-50 hover:bg-gray-100 border border-gray-200 rounded transition-colors"
    >
      <Edit2 size={12} className="mr-1" /> Edit
    </button>
    <button
      type="button"
      onClick={onDelete}
      className="flex items-center justify-center py-1.5 text-red-600 bg-red-50 hover:bg-red-100 border border-red-100 rounded transition-colors"
    >
      <Trash2 size={12} />
    </button>
  </div>
);

export const MenuItemCard: React.FC<MenuItemCardProps> = ({
  canManageMenu = true,
  item,
  onEdit,
  onDelete,
  formatPrice,
}) => {
  return (
    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden hover:shadow-md transition-shadow group flex flex-col">
      <div className="relative aspect-[4/3] bg-gray-100 overflow-hidden shrink-0">
        <ItemImage imageUrl={item.image_url} name={item.name} />
        <StatusBadge status={item.status} />
      </div>

      <div className="p-3 flex flex-col flex-1">
        <div className="mb-2">
          <h4 className="font-semibold text-gray-900 text-sm truncate leading-tight">
            {item.name}
          </h4>
          <div className="flex items-baseline justify-between mt-1">
            <span className="text-base font-bold text-pos-primary">
              {formatPrice(item.base_price)}
            </span>
          </div>
        </div>

        {canManageMenu ? (
          <ActionButtons
            onEdit={() => onEdit(item)}
            onDelete={() => onDelete(item)}
          />
        ) : null}
      </div>
    </div>
  );
};
